// File: src/context/AuthProvider.jsx
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { authApi } from "@/lib/axios";

/* ──────────────────────────────────────────────────────────────
 * Context
 *
 * Authentication authority:
 *
 *   HttpOnly session cookie
 *          ↓
 *      GET /auth/me
 *          ↓
 *      AuthProvider
 *
 * There is intentionally NO browser-readable JWT authority.
 * ────────────────────────────────────────────────────────────── */

const AuthContext = createContext({
  user: null,
  role: null,
  roles: [],
  permissions: [],

  /*
   * loading:
   * login/logout/password-action UI state.
   *
   * bootstrapping:
   * initial server session validation.
   *
   * ready:
   * safe for route rendering.
   */
  loading: false,
  bootstrapping: true,
  ready: false,
  authenticated: false,

  login: async () => ({
    ok: false,
  }),

  logout: async () => {},

  refreshSession: async () => ({
    ok: false,
  }),

  requestPasswordReset: async () => ({
    ok: false,
  }),

  confirmPasswordReset: async () => ({
    ok: false,
  }),

  hasRole: () => false,
  hasAnyRole: () => false,

  hasPermission: () => false,
  hasAnyPermission: () => false,

  inCostCentre: () => false,
});

/* ──────────────────────────────────────────────────────────────
 * Basic normalization
 * ────────────────────────────────────────────────────────────── */

function lower(value) {
  return value == null
    ? null
    : String(value)
        .trim()
        .toLowerCase();
}

function normalizeArray(value) {
  if (!value) {
    return [];
  }

  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value === "string") {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
}

function normalizeStringArray(value) {
  return Array.from(
    new Set(
      normalizeArray(value)
        .map(lower)
        .filter(Boolean)
    )
  );
}

function normalizePositiveIds(value) {
  return Array.from(
    new Set(
      normalizeArray(value)
        .map(Number)
        .filter(
          (id) =>
            Number.isInteger(id) &&
            id > 0
        )
    )
  );
}

/* ──────────────────────────────────────────────────────────────
 * Legacy browser security-state cleanup
 *
 * These values may exist from previous frontend deployments.
 *
 * They are deleted proactively and are NEVER read.
 * ────────────────────────────────────────────────────────────── */

const LEGACY_AUTHORITY_KEYS = [
  // Historical JWT mirrors
  "auth:token",
  "token",
  "access_token",
  "jwt",
  "Authorization",

  // Historical user / role authority
  "auth:user",
  "role",
  "roles",
  "permissions",
  "scopes",
  "groups",
  "is_platform_admin",

  // Historical browser platform-admin context
  "solo:admin-context",
];

function clearLegacyAuthorityStorage() {
  try {
    for (const key of LEGACY_AUTHORITY_KEYS) {
      localStorage.removeItem(key);
    }
  } catch {
    // Browser storage cleanup must never break authentication.
  }
}

/* ──────────────────────────────────────────────────────────────
 * Unit convenience state
 *
 * These values exist only for frontend UX compatibility.
 *
 * They are NOT authentication, authorization, tenant, or
 * platform-admin authority.
 *
 * The backend session and tenant middleware remain authoritative.
 * ────────────────────────────────────────────────────────────── */

const UNIT_KEY =
  "unit:slug";

const CURRENT_UNIT_KEY =
  "solo:current-unit";

function clearUnitContext() {
  try {
    localStorage.removeItem(
      CURRENT_UNIT_KEY
    );

    localStorage.removeItem(
      UNIT_KEY
    );

    localStorage.removeItem(
      "activeUnitId"
    );

    localStorage.removeItem(
      "activeUnitSlug"
    );
  } catch {
    // noop
  }
}

function seedUnitConvenienceFromUser(user) {
  if (!user) {
    return;
  }

  try {
    const companyId =
      user.company_id != null
        ? Number(user.company_id)
        : null;

    const unitId =
      user.unit_id != null
        ? Number(user.unit_id)
        : null;

    const unitSlug =
      user.unit_slug
        ? String(user.unit_slug)
        : "";

    /*
     * Login convenience only.
     *
     * Company ID remains part of the current login architecture
     * until login-v2 / global identities are implemented.
     */
    if (
      Number.isInteger(companyId) &&
      companyId > 0
    ) {
      localStorage.setItem(
        "auth:lastCompanyId",
        String(companyId)
      );
    }

    if (user.email) {
      localStorage.setItem(
        "auth:lastEmail",
        String(user.email)
      );
    }

    if (
      Number.isInteger(companyId) &&
      companyId > 0 &&
      Number.isInteger(unitId) &&
      unitId > 0
    ) {
      const context = {
        id: unitId,
        company_id: companyId,
        slug: unitSlug,
      };

      localStorage.setItem(
        CURRENT_UNIT_KEY,
        JSON.stringify(context)
      );

      localStorage.setItem(
        "activeUnitId",
        String(unitId)
      );

      if (unitSlug) {
        localStorage.setItem(
          UNIT_KEY,
          unitSlug
        );

        localStorage.setItem(
          "activeUnitSlug",
          unitSlug
        );
      } else {
        localStorage.removeItem(
          UNIT_KEY
        );

        localStorage.removeItem(
          "activeUnitSlug"
        );
      }
    } else {
      /*
       * Never leave an old unit selected when the verified session
       * does not contain a valid unit.
       */
      clearUnitContext();
    }
  } catch {
    // Convenience state must never break auth.
  }
}

/* ──────────────────────────────────────────────────────────────
 * Server user normalization
 * ────────────────────────────────────────────────────────────── */

function readPlatformAdminFlag(snapshot) {
  if (!snapshot) {
    return false;
  }

  /*
   * These compatibility property names are accepted only from the
   * server response handled by AuthProvider.
   *
   * No browser storage/JWT fallback exists.
   */
  return (
    snapshot.is_platform_admin === true ||
    snapshot.isPlatformAdmin === true ||
    snapshot.platform_admin === true ||
    snapshot.platformAdmin === true
  );
}

function normalizeUserSnapshot(snapshot) {
  if (
    !snapshot ||
    typeof snapshot !== "object"
  ) {
    return null;
  }

  const role =
    lower(snapshot.role);

  const roles =
    normalizeStringArray(
      snapshot.roles
    );

  const permissions =
    normalizeStringArray(
      snapshot.permissions
    );

  const mergedRoles =
    Array.from(
      new Set([
        ...(role ? [role] : []),
        ...roles,
      ])
    );

  const allowedCostCentres =
    normalizePositiveIds(
      snapshot.allowedCostCentres ??
      snapshot.cost_centres
    );

  return {
    ...snapshot,

    role:
      role || null,

    roles:
      mergedRoles,

    permissions,

    allowedCostCentres,

    is_platform_admin:
      readPlatformAdminFlag(
        snapshot
      ),
  };
}

/* ──────────────────────────────────────────────────────────────
 * Provider
 * ────────────────────────────────────────────────────────────── */

export function AuthProvider({
  children,
}) {
  /*
   * CRITICAL:
   *
   * We deliberately do NOT initialize user from localStorage.
   *
   * Every browser load starts with:
   *
   *   user = null
   *
   * until /auth/me validates the server session.
   */
  const [user, setUser] =
    useState(null);

  const [loading, setLoading] =
    useState(false);

  const [
    bootstrapping,
    setBootstrapping,
  ] =
    useState(true);

  const bootstrapRef =
    useRef(false);

  const mountedRef =
    useRef(true);

  /* ────────────────────────────────────────────────────────────
   * Purge legacy browser security state
   * ──────────────────────────────────────────────────────────── */

  useEffect(() => {
    clearLegacyAuthorityStorage();

    return () => {
      mountedRef.current =
        false;
    };
  }, []);

  /* ────────────────────────────────────────────────────────────
   * Local session cleanup
   *
   * This removes browser UI state.
   *
   * Real cookie invalidation is performed by backend logout /
   * password reset endpoints.
   * ──────────────────────────────────────────────────────────── */

  const clearLocalSession =
    useCallback(
      ({
        clearUnit = true,
      } = {}) => {
        clearLegacyAuthorityStorage();

        if (clearUnit) {
          clearUnitContext();
        }

        if (mountedRef.current) {
          setUser(null);
        }
      },
      []
    );

  /* ────────────────────────────────────────────────────────────
   * Server-authoritative session refresh
   * ──────────────────────────────────────────────────────────── */

  const refreshSession =
    useCallback(
      async ({
        quiet = false,
      } = {}) => {
        if (
          !quiet &&
          mountedRef.current
        ) {
          setBootstrapping(
            true
          );
        }

        try {
          /*
           * HttpOnly cookies are automatically sent by authApi because
           * axios uses withCredentials: true.
           *
           * No JavaScript-readable token is required.
           */
          const { data } =
  await authApi.get("/me");

          const snapshot =
            data?.user ??
            null;

          const normalized =
            normalizeUserSnapshot(
              snapshot
            );

          if (!normalized?.id) {
            clearLocalSession();

            return {
              ok: false,
              authenticated: false,
            };
          }

          if (mountedRef.current) {
            setUser(
              normalized
            );

            seedUnitConvenienceFromUser(
              normalized
            );
          }

          return {
            ok: true,
            authenticated: true,
            user: normalized,
          };
        } catch (error) {
          const status =
            error?.response?.status;

          /*
           * 401/403 from /auth/me means the server does not recognize
           * this browser as having a usable authenticated session.
           *
           * Browser state cannot override that result.
           */
          if (
            status === 401 ||
            status === 403
          ) {
            clearLocalSession();

            return {
              ok: false,
              authenticated: false,
              status,
            };
          }

          /*
           * Network/server failure is different from confirmed logout.
           *
           * We still do not resurrect a cached user from localStorage.
           */
          return {
            ok: false,
            authenticated: false,
            status:
              status || null,

            error:
              error?.message ||
              "Unable to validate session",
          };
        } finally {
          if (
            mountedRef.current &&
            !quiet
          ) {
            setBootstrapping(
              false
            );
          }
        }
      },
      [
        clearLocalSession,
      ]
    );

  /* ────────────────────────────────────────────────────────────
   * Initial session bootstrap
   *
   * Always ask the server.
   *
   * There is intentionally no:
   *
   *   if (!token) return;
   * ──────────────────────────────────────────────────────────── */

  useEffect(() => {
    if (
      bootstrapRef.current
    ) {
      return;
    }

    bootstrapRef.current =
      true;

    refreshSession().catch(
      () => {
        if (
          mountedRef.current
        ) {
          setBootstrapping(
            false
          );
        }
      }
    );
  }, [
    refreshSession,
  ]);

  /* ────────────────────────────────────────────────────────────
   * Forced logout event
   *
   * axios emits this when /auth/me confirms that the session
   * is invalid.
   * ──────────────────────────────────────────────────────────── */

  useEffect(() => {
    const onForceLogout =
      () => {
        clearLocalSession();

        if (mountedRef.current) {
          setBootstrapping(
            false
          );
        }
      };

    window.addEventListener(
      "auth:force-logout",
      onForceLogout
    );

    return () => {
      window.removeEventListener(
        "auth:force-logout",
        onForceLogout
      );
    };
  }, [
    clearLocalSession,
  ]);

  /* ────────────────────────────────────────────────────────────
   * Login
   *
   * Current backend architecture still requires company_id.
   *
   * Email-only login remains a separate later identity/membership
   * migration and is intentionally NOT mixed into this change.
   * ──────────────────────────────────────────────────────────── */

  const login =
    useCallback(
      async ({
        email,
        password,
        company_id,
        unit_id,
        unit_slug,
      }) => {
        if (mountedRef.current) {
          setLoading(true);
        }

        try {
          const payload = {
            email:
              String(
                email || ""
              ).trim(),

            password,

            company_id:
              company_id != null
                ? String(
                    company_id
                  ).trim()
                : undefined,

            ...(unit_id != null
              ? {
                  unit_id,
                }
              : {}),

            ...(unit_slug
              ? {
                  unit_slug:
                    String(
                      unit_slug
                    ),
                }
              : {}),
          };

          /*
           * Successful login sets HttpOnly auth/unit cookies.
           *
           * We intentionally ignore any JWT the backend may still return
           * during this deployment transition.
           */
          await authApi.post(
            "/login",
            payload
          );

          /*
           * Login response is not our final identity authority.
           *
           * Immediately verify the newly established cookie session
           * through /auth/me.
           */
          const session =
            await refreshSession({
              quiet: true,
            });

          if (
            !session.ok ||
            !session.user
          ) {
            clearLocalSession();

            return {
              ok: false,
              error:
                "Login succeeded but the authenticated session could not be verified.",
            };
          }

          const verifiedUser =
            session.user;

          /*
           * Convenience only for the existing LoginPage.
           *
           * This does not grant tenant access.
           */
          if (
            company_id != null
          ) {
            try {
              localStorage.setItem(
                "auth:lastCompanyId",
                String(
                  company_id
                )
              );
            } catch {
              // noop
            }
          }

          return {
            ok: true,

            user:
              verifiedUser,

            must_change_password:
              Boolean(
                verifiedUser.must_change_password
              ),
          };
        } catch (error) {
          const status =
            error?.response?.status;

          const data =
            error?.response?.data;

          /*
           * Multi-unit login selection.
           */
          if (
            status === 409 &&
            data?.error ===
              "UNIT_REQUIRED" &&
            Array.isArray(
              data?.units
            )
          ) {
            try {
              window.dispatchEvent(
                new CustomEvent(
                  "auth:unit-required",
                  {
                    detail: {
                      units:
                        data.units,
                    },
                  }
                )
              );
            } catch {
              // noop
            }

            return {
              ok: false,
              error:
                "UNIT_REQUIRED",
              units:
                data.units,
            };
          }

          /*
           * Requested unit does not belong to the authenticated
           * company/user flow.
           */
          if (
            status === 400 &&
            (
              data?.error ===
                "INVALID_UNIT" ||
              String(
                data?.error ||
                ""
              ).includes(
                "INVALID_UNIT"
              )
            )
          ) {
            const units =
              Array.isArray(
                data?.units
              )
                ? data.units
                : [];

            clearUnitContext();

            try {
              window.dispatchEvent(
                new CustomEvent(
                  "auth:invalid-unit",
                  {
                    detail: {
                      units,
                    },
                  }
                )
              );
            } catch {
              // noop
            }

            return {
              ok: false,
              error:
                "INVALID_UNIT",
              units,
            };
          }

          const message =
            data?.error ||
            data?.message ||
            error?.message ||
            "Login failed";

          return {
            ok: false,
            error:
              message,
          };
        } finally {
          if (
            mountedRef.current
          ) {
            setLoading(false);
            setBootstrapping(false);
          }
        }
      },
      [
        clearLocalSession,
        refreshSession,
      ]
    );

  /* ────────────────────────────────────────────────────────────
   * Logout
   * ──────────────────────────────────────────────────────────── */

  const logout =
    useCallback(
      async () => {
        if (mountedRef.current) {
          setLoading(true);
        }

        try {
          /*
           * Backend clears:
           *
           * - auth cookie(s)
           * - unit cookie(s)
           * - signed admin_ctx
           * - legacy admin_ctx cookie
           */
          await authApi.post(
            "/logout"
          );
        } catch {
          /*
           * A network failure cannot leave privileged browser UI
           * active.
           */
        } finally {
          clearLocalSession();

          if (mountedRef.current) {
            setBootstrapping(false);
            setLoading(false);
          }
        }
      },
      [
        clearLocalSession,
      ]
    );

  /* ────────────────────────────────────────────────────────────
   * Password reset request
   *
   * Current backend still requires company_id.
   * ──────────────────────────────────────────────────────────── */

  const requestPasswordReset =
    useCallback(
      async ({
        email,
        company_id,
      }) => {
        try {
          const companyId =
            Number(
              String(
                company_id || ""
              ).trim()
            );

          const emailNorm =
            String(
              email || ""
            ).trim();

          if (
            !emailNorm ||
            !Number.isInteger(
              companyId
            ) ||
            companyId <= 0
          ) {
            return {
              ok: false,

              error:
                "Email and Company ID are required.",
            };
          }

          await authApi.post(
            "/password-reset/request",
            {
              email:
                emailNorm,

              company_id:
                companyId,
            }
          );

          /*
           * Generic success reduces account enumeration.
           */
          return {
            ok: true,
          };
        } catch {
          return {
            ok: true,
          };
        }
      },
      []
    );

  /* ────────────────────────────────────────────────────────────
   * Password reset confirmation
   * ──────────────────────────────────────────────────────────── */

  const confirmPasswordReset =
    useCallback(
      async ({
        email,
        company_id,
        token:
          resetToken,
        new_password,
      }) => {
        try {
          const companyId =
            Number(
              String(
                company_id || ""
              ).trim()
            );

          const emailNorm =
            String(
              email || ""
            ).trim();

          const resetTokenNorm =
            String(
              resetToken || ""
            ).trim();

          const password =
            String(
              new_password || ""
            );

          if (
            !emailNorm ||
            !resetTokenNorm ||
            !password ||
            !Number.isInteger(
              companyId
            ) ||
            companyId <= 0
          ) {
            return {
              ok: false,

              error:
                "Email, token, Company ID, and new password are required.",
            };
          }

          const { data } =
            await authApi.post(
              "/password-reset/confirm",
              {
                email:
                  emailNorm,

                company_id:
                  companyId,

                token:
                  resetTokenNorm,

                new_password:
                  password,
              }
            );

          /*
           * Backend invalidates session cookies after password reset.
           */
          clearLocalSession();

          return {
            ok: true,
            data,
          };
        } catch (error) {
          const message =
            error?.response?.data
              ?.error ||
            error?.response?.data
              ?.message ||
            "Invalid token or expired. Please request a new reset link.";

          return {
            ok: false,
            error:
              message,
          };
        }
      },
      [
        clearLocalSession,
      ]
    );

  /* ────────────────────────────────────────────────────────────
   * Server-derived authorization helpers
   * ──────────────────────────────────────────────────────────── */

  const role =
    user?.role ||
    null;

  const roles =
    useMemo(() => {
      if (!user) {
        return [];
      }

      return Array.from(
        new Set([
          ...(user.role
            ? [
                lower(
                  user.role
                ),
              ]
            : []),

          ...normalizeStringArray(
            user.roles
          ),
        ])
      ).filter(Boolean);
    }, [
      user,
    ]);

  const permissions =
    useMemo(
      () =>
        normalizeStringArray(
          user?.permissions
        ),
      [
        user,
      ]
    );

  const roleSet =
    useMemo(
      () =>
        new Set(
          roles
        ),
      [
        roles,
      ]
    );

  const permissionSet =
    useMemo(
      () =>
        new Set(
          permissions
        ),
      [
        permissions,
      ]
    );

  const hasRole =
    useCallback(
      (wantedRole) => {
        if (!user) {
          return false;
        }

        const wanted =
          normalizeStringArray(
            wantedRole
          );

        /*
         * Security helper with no requested role fails closed.
         */
        if (
          wanted.length === 0
        ) {
          return false;
        }

        return wanted.some(
          (candidate) =>
            roleSet.has(
              candidate
            )
        );
      },
      [
        user,
        roleSet,
      ]
    );

  const hasAnyRole =
    useCallback(
      (wantedRoles = []) => {
        if (!user) {
          return false;
        }

        const wanted =
          normalizeStringArray(
            wantedRoles
          );

        if (
          wanted.length === 0
        ) {
          return false;
        }

        return wanted.some(
          (candidate) =>
            roleSet.has(
              candidate
            )
        );
      },
      [
        user,
        roleSet,
      ]
    );

  const hasPermission =
    useCallback(
      (wantedPermission) => {
        if (!user) {
          return false;
        }

        const wanted =
          normalizeStringArray(
            wantedPermission
          );

        if (
          wanted.length === 0
        ) {
          return false;
        }

        return wanted.some(
          (candidate) =>
            permissionSet.has(
              candidate
            )
        );
      },
      [
        user,
        permissionSet,
      ]
    );

  const hasAnyPermission =
    useCallback(
      (wantedPermissions = []) => {
        if (!user) {
          return false;
        }

        const wanted =
          normalizeStringArray(
            wantedPermissions
          );

        if (
          wanted.length === 0
        ) {
          return false;
        }

        return wanted.some(
          (candidate) =>
            permissionSet.has(
              candidate
            )
        );
      },
      [
        user,
        permissionSet,
      ]
    );

  const inCostCentre =
    useCallback(
      (ccId) => {
        if (!user) {
          return false;
        }

        const id =
          Number(ccId);

        if (
          !Number.isInteger(id) ||
          id <= 0
        ) {
          return false;
        }

        /*
         * Unrestricted access must be explicit.
         *
         * Missing/empty allowedCostCentres never means "all".
         */
        if (
          user.cost_centre_scope ===
            "all" ||
          user.all_cost_centres ===
            true ||
          user.has_all_cost_centres ===
            true
        ) {
          return true;
        }

        const allowed =
          normalizePositiveIds(
            user.allowedCostCentres
          );

        return allowed.includes(
          id
        );
      },
      [
        user,
      ]
    );

  /* ────────────────────────────────────────────────────────────
   * Derived session state
   * ──────────────────────────────────────────────────────────── */

  const authenticated =
    Boolean(
      user?.id
    );

  const ready =
    !bootstrapping;

  /* ────────────────────────────────────────────────────────────
   * Context value
   * ──────────────────────────────────────────────────────────── */

  const value =
    useMemo(
      () => ({
        user,

        role,
        roles,
        permissions,

        loading,
        bootstrapping,
        ready,
        authenticated,

        login,
        logout,
        refreshSession,

        requestPasswordReset,
        confirmPasswordReset,

        hasRole,
        hasAnyRole,

        hasPermission,
        hasAnyPermission,

        inCostCentre,
      }),
      [
        user,
        role,
        roles,
        permissions,

        loading,
        bootstrapping,
        ready,
        authenticated,

        login,
        logout,
        refreshSession,

        requestPasswordReset,
        confirmPasswordReset,

        hasRole,
        hasAnyRole,

        hasPermission,
        hasAnyPermission,

        inCostCentre,
      ]
    );

  return (
    <AuthContext.Provider
      value={value}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(
    AuthContext
  );
}